import {
  TOOL_IGNORE_LIST,
  type BaseStreamEvent,
  type ToolCallEvent,
  type UserChatEvent,
  type ModelChatEvent,
  type ErrorEvent,
  type WarningEvent,
  type CancelledEvent,
  type MessagePersistedEvent,
} from "~/components/assistant/lib/events";
import type {
  ProcessingOptions,
  ProcessingResult,
} from "~/components/assistant/lib/messageTypeRegistry";
import type { Message, MessagePart, ToolCall } from "~/components/assistant/types";

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert value to string for processing
 */
function stringify(value: unknown): string {
  return typeof value === "string" ? value : value ? JSON.stringify(value) : "";
}

/**
 * Check if a tool should be ignored based on the ignore list
 */
function shouldIgnoreTool(tool: string | undefined, debugMode: boolean | undefined): boolean {
  if (debugMode || !tool) {
    return false;
  }
  return TOOL_IGNORE_LIST.some((prefix) => tool.startsWith(prefix));
}

/**
 * Find the agent message that should be updated for the current event.
 * Uses trace_id to match history messages, or finds the streaming optimistic message.
 */
export function findTargetAgentMessage(messages: Message[], traceId?: string): Message | undefined {
  // Search backwards (most recent messages first)
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.kind !== "model_chat") {
      continue;
    }

    // Match by trace ID (for history messages that already have trace IDs)
    if (traceId && msg.traceId === traceId) {
      return msg;
    }

    // Match optimistic message (streaming, no trace ID yet)
    // This handles the case where API events start arriving with a trace_id
    // for a message we created optimistically
    if (msg.streaming && !msg.traceId) {
      return msg;
    }
  }

  return undefined;
}

/**
 * Find optimistic user message by content match
 */
export function findOptimisticUserMessage(
  messages: Message[],
  content: string
): { message: Message; index: number } | null {
  // Search backwards (most recent messages first)
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.kind === "user_chat" && !msg.traceId && msg.content === content) {
      return { message: msg, index: i };
    }
    // Stop searching if we hit a user message with a trace ID (previous conversation)
    if (msg.kind === "user_chat" && msg.traceId) {
      break;
    }
  }
  return null;
}

/**
 * Create a new agent message with default values
 */
function createAgentMessage(
  event: BaseStreamEvent,
  options: ProcessingOptions,
  overrides?: Partial<Message>
): Message {
  return {
    id: options.nextMessageId(),
    content: "",
    kind: "model_chat",
    timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
    parts: [],
    streaming: false,
    traceId: event.trace_id || options.traceId,
    ...overrides,
  };
}

/**
 * Create a new user message
 */
function createUserMessage(event: UserChatEvent, options: ProcessingOptions): Message {
  return {
    id: event.id || options.nextMessageId(),
    content: event.content || "",
    kind: "user_chat",
    timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
    traceId: event.trace_id,
  };
}

/**
 * Find or create an agent message for the given event
 */
function findOrCreateAgentMessage(
  messages: Message[],
  event: BaseStreamEvent,
  options: ProcessingOptions,
  createOverrides?: Partial<Message>
): { messages: Message[]; targetMessage: Message } {
  const targetMessage = findTargetAgentMessage(messages, event.trace_id);

  if (targetMessage) {
    return { messages: [...messages], targetMessage };
  }

  // Create new agent message
  const newMessage = createAgentMessage(event, options, createOverrides);
  return { messages: [...messages, newMessage], targetMessage: newMessage };
}

/**
 * Update a specific message in the messages array immutably
 */
export function updateMessageInArray(
  messages: Message[],
  messageId: string,
  updater: (message: Message) => Message
): Message[] {
  return messages.map((m) => (m.id === messageId ? updater(m) : m));
}

/**
 * Add parts to a message's parts array
 */
export function addPartsToMessage(message: Message, newParts: MessagePart[]): MessagePart[] {
  const existingParts = message.parts || [];
  const nextOrder = existingParts.length;
  return [...existingParts, ...newParts.map((part, idx) => ({ ...part, order: nextOrder + idx }))];
}

/**
 * Mark all running tool calls as cancelled and add a cancelled part if not present.
 * Returns a new parts array with the modifications applied.
 */
export function markPartsAsCancelled(parts: MessagePart[]): MessagePart[] {
  // Mark all running tool calls as cancelled
  const updatedParts = parts.map((part) => {
    if (part.type === "toolCall" && part.toolCall?.running) {
      return {
        ...part,
        toolCall: { ...part.toolCall, running: false, cancelled: true },
      };
    }
    return part;
  });

  // Add cancelled part if not already present
  const hasCancelledPart = updatedParts.some((p) => p.type === "cancelled");
  if (!hasCancelledPart) {
    updatedParts.push({ type: "cancelled", order: updatedParts.length });
  }

  return updatedParts;
}

// ============================================================================
// Event Processors
// ============================================================================

/**
 * Process tool_call and tool_return events from the stream.
 * Creates or updates tool call items in agent messages.
 */
export function processToolMessage(
  messages: Message[],
  event: ToolCallEvent,
  options: ProcessingOptions
): ProcessingResult {
  // Ignore blacklisted tools (unless debug mode is enabled)
  if (shouldIgnoreTool(event.tool, options.debugMode)) {
    return { messages: [...messages] };
  }

  const callId = event.tool_call_id;
  if (!callId) {
    return { messages: [...messages] };
  }

  const stringArgs = stringify(event.args);
  const stringContent = stringify(event.content);

  if (!stringArgs && !stringContent && !event.tool) {
    return { messages: [...messages] };
  }

  // Find or create the agent message to hold tool output
  const { messages: updatedMessages, targetMessage } = findOrCreateAgentMessage(
    messages,
    event,
    options
  );

  const isToolReturn = event.kind === "tool_return";

  // Update the agent message with tool call information
  const finalMessages = updateMessageInArray(updatedMessages, targetMessage.id, (m) => {
    const parts: MessagePart[] = [...(m.parts || [])];

    const existingIndex = parts.findIndex(
      (p) => p.type === "toolCall" && p.toolCall?.tool_call_id === callId
    );

    // Get or create tool call
    const toolCall: ToolCall =
      existingIndex !== -1
        ? { ...parts[existingIndex].toolCall! }
        : {
            tool_call_id: callId,
            tool: "",
            toolName: "",
            args: {},
            content: {},
            timestamp: "",
            running: true,
          };

    // Update tool call with args
    if (stringArgs) {
      try {
        const parsedArgs = JSON.parse(stringArgs);
        toolCall.args = { ...toolCall.args, ...parsedArgs };
      } catch {
        toolCall.args = { ...toolCall.args, raw: stringArgs };
      }
    }

    // Update tool call with content
    if (stringContent) {
      try {
        const parsedContent = JSON.parse(stringContent);
        toolCall.content = { ...toolCall.content, ...parsedContent };
      } catch {
        toolCall.content = { ...toolCall.content, raw: stringContent };
      }
    }

    // Mark as completed when we receive tool_return
    if (isToolReturn) {
      toolCall.running = false;
    }

    toolCall.tool = event.tool || toolCall.tool;
    toolCall.toolName = event.tool_name || toolCall.toolName || "(unknown)";
    toolCall.timestamp = event.timestamp ?? toolCall.timestamp;

    /**
     * FIXME: Temporary hack to ensure only one tool call per tool name shows progress at a time.
     * The API should return a proper error when a tool fails to complete. Currently, orphaned
     * tool calls never resolve, leaving us with no indication of whether they succeeded or failed.
     */
    if (existingIndex === -1 && toolCall.toolName) {
      parts.forEach((part) => {
        if (
          part.type === "toolCall" &&
          part.toolCall &&
          part.toolCall.toolName === toolCall.toolName &&
          part.toolCall.tool_call_id !== callId
        ) {
          part.toolCall.running = false;
        }
      });
    }

    // Add or update tool call in parts
    if (existingIndex === -1) {
      parts.push({ type: "toolCall", toolCall, order: parts.length });
    } else {
      parts[existingIndex] = { ...parts[existingIndex], toolCall };
    }

    // Update trace ID if event has one and message doesn't (optimistic message case)
    const traceId = event.trace_id || m.traceId;

    return { ...m, parts, traceId };
  });

  return { messages: finalMessages };
}

/**
 * Process user_chat events from the stream.
 * Updates existing optimistic user message if found, otherwise creates new one.
 */
export function processUserMessage(
  messages: Message[],
  event: UserChatEvent,
  options: ProcessingOptions
): ProcessingResult | null {
  const incomingContent = event.content || "";
  const incomingTraceId = event.trace_id;

  // Find optimistic user message
  const optimistic = findOptimisticUserMessage(messages, incomingContent);

  if (optimistic) {
    // Update existing optimistic message with trace ID
    return {
      messages: updateMessageInArray(messages, optimistic.message.id, (m) => ({
        ...m,
        traceId: incomingTraceId,
      })),
    };
  }

  // No optimistic message - create new user message (history loading case)
  const userMsg = createUserMessage(event, options);
  return { messages: [...messages, userMsg] };
}

/**
 * Process model_chat events from the stream.
 * Handles content, figures, dataframes, values, filters, and feedback metadata.
 */
export function processModelMessage(
  messages: Message[],
  event: ModelChatEvent,
  options: ProcessingOptions
): ProcessingResult {
  const hasContent = Boolean(event.content);
  const hasFigures = Array.isArray(event.figures) && event.figures.length > 0;
  const hasDataframes = Array.isArray(event.dataframes) && event.dataframes.length > 0;
  const hasValues = Array.isArray(event.values) && event.values.length > 0;
  const hasTextFiles = Array.isArray(event.text_files) && event.text_files.length > 0;
  const hasBlobFiles = Array.isArray(event.blob_files) && event.blob_files.length > 0;
  const hasFilters = Boolean(event.filters);

  if (
    !hasContent &&
    !hasFigures &&
    !hasDataframes &&
    !hasValues &&
    !hasTextFiles &&
    !hasBlobFiles &&
    !hasFilters
  ) {
    return { messages: [...messages] };
  }

  // Use message_id from backend if available (for chat history), otherwise generate client-side ID
  const isHistoryMessage = Boolean(event.message_id);
  const { messages: updatedMessages, targetMessage } = findOrCreateAgentMessage(
    messages,
    event,
    options,
    {
      id: event.message_id || options.nextMessageId(),
      messageFeedback: event.message_feedback || null,
      feedbackReady: isHistoryMessage,
    }
  );

  // Build new parts to add
  const newParts: MessagePart[] = [];

  if (hasContent) {
    newParts.push({ type: "content", content: event.content!, order: 0 });
  }

  if (hasFigures) {
    for (const fig of event.figures!) {
      newParts.push({ type: "figure", figure: fig as MessagePart["figure"], order: 0 });
    }
  }

  if (hasDataframes) {
    for (const df of event.dataframes!) {
      newParts.push({ type: "dataframe", dataframe: df as MessagePart["dataframe"], order: 0 });
    }
  }

  if (hasValues) {
    for (const val of event.values!) {
      newParts.push({ type: "value", value: val as MessagePart["value"], order: 0 });
    }
  }

  if (hasTextFiles) {
    for (const tf of event.text_files!) {
      newParts.push({ type: "text_file", textFile: tf as MessagePart["textFile"], order: 0 });
    }
  }

  if (hasBlobFiles) {
    for (const bf of event.blob_files!) {
      newParts.push({ type: "blob_file", blobFile: bf as MessagePart["blobFile"], order: 0 });
    }
  }

  if (hasFilters) {
    newParts.push({ type: "filters", filters: event.filters as MessagePart["filters"], order: 0 });
  }

  // Update the message with new parts and metadata
  const finalMessages = updateMessageInArray(updatedMessages, targetMessage.id, (m) => {
    const parts = addPartsToMessage(m, newParts);
    const traceId = event.trace_id || m.traceId;
    const messageId = event.message_id || m.id;
    const messageFeedback =
      event.message_feedback !== undefined ? event.message_feedback : m.messageFeedback;
    const feedbackReady = isHistoryMessage ? true : m.feedbackReady;

    return {
      ...m,
      id: messageId,
      parts,
      traceId,
      messageFeedback,
      feedbackReady,
    };
  });

  // Include filters in result if present (for auto-apply callback)
  return { messages: finalMessages, filters: hasFilters ? event.filters : undefined };
}

/**
 * Process error events from the stream.
 * Removes optimistic messages and adds error message.
 */
export function processErrorMessage(
  messages: Message[],
  event: ErrorEvent,
  _options: ProcessingOptions
): ProcessingResult {
  // Find and remove the optimistic agent message if it exists
  const targetMessage = findTargetAgentMessage(messages, event.trace_id);
  const filtered = targetMessage ? messages.filter((m) => m.id !== targetMessage.id) : messages;

  // Add error message with trace ID
  const errorMessage: Message = {
    id: `error-${Date.now()}`,
    content: "",
    kind: "model_chat",
    timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
    error: event.message || event.error || "An error occurred",
    traceId: event.trace_id,
  };

  return { messages: [...filtered, errorMessage] };
}

/**
 * Process warning events from the stream.
 * Removes optimistic messages and adds warning message.
 */
export function processWarningMessage(
  messages: Message[],
  event: WarningEvent,
  _options: ProcessingOptions
): ProcessingResult {
  // Find and remove the optimistic agent message if it exists
  const targetMessage = findTargetAgentMessage(messages, event.trace_id);
  const filtered = targetMessage ? messages.filter((m) => m.id !== targetMessage.id) : messages;

  // Add warning message with trace ID
  const warningMessage: Message = {
    id: `warning-${Date.now()}`,
    content: "",
    kind: "model_chat",
    timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
    warning: event.message || "A warning occurred",
    traceId: event.trace_id,
  };

  return { messages: [...filtered, warningMessage] };
}

/**
 * Process cancelled events from the stream.
 * Marks running tool calls as cancelled and adds cancellation message.
 * If no existing agent message exists (e.g., cancelled before any response),
 * creates a new cancelled message.
 */
export function processCancelledMessage(
  messages: Message[],
  event: CancelledEvent,
  options: ProcessingOptions
): ProcessingResult {
  // Find the streaming agent message
  const targetMessage = findTargetAgentMessage(messages, event.trace_id);

  if (!targetMessage) {
    // No existing agent message - create a new cancelled message
    // This happens when loading history where stream was cancelled before any response
    const cancelledMessage: Message = {
      id: options.nextMessageId(),
      content: "",
      kind: "model_chat",
      timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
      parts: [{ type: "cancelled", order: 0 }],
      streaming: false,
      cancelled: true,
      traceId: event.trace_id,
    };
    return { messages: [...messages, cancelledMessage] };
  }

  const updatedMessages = updateMessageInArray(messages, targetMessage.id, (m) => ({
    ...m,
    parts: markPartsAsCancelled(m.parts || []),
    streaming: false,
    cancelled: true,
    traceId: event.trace_id || m.traceId,
  }));

  return { messages: updatedMessages };
}

/**
 * Process message_persisted events from the stream.
 * Updates the message ID from the optimistic client ID to the database UUID
 * and marks the message as ready for feedback.
 */
export function processMessagePersisted(
  messages: Message[],
  event: MessagePersistedEvent,
  _options: ProcessingOptions
): ProcessingResult {
  const clientMessageId = event.client_message_id;
  const databaseMessageId = event.message_id;

  if (!clientMessageId || !databaseMessageId) {
    return { messages: [...messages] };
  }

  // Find the message by client ID and update it
  const updatedMessages = updateMessageInArray(messages, clientMessageId, (m) => ({
    ...m,
    id: databaseMessageId,
    feedbackReady: true,
  }));

  return { messages: updatedMessages };
}
